import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { CustomTableCell } from './CustomTableCell'
import EditIcon from '@mui/icons-material/EditOutlined'
import DoneIcon from '@mui/icons-material/DoneAllTwoTone'
import { updateAttackData, updateAttackDataByGuid } from '../../actions'
import { useMutation } from '@apollo/react-hooks'
import { SAVE_ATTACK, SAVE_ATTACK_BY_GUID } from '../../queries/attack'
import { vehicleTypes } from '../../../../lib/util'
import { kineticEnergy } from '../../../../lib/util'

function createData(a) {
  let lineColor
  if (a.original_id) {
    try {
      const parsedGeojson = JSON.parse(a.geojson || '{}')
      lineColor =
        a.line_color || parsedGeojson?.properties?.line_color || '#ff0000'
    } catch (e) {
      lineColor = a.line_color || '#ff0000'
    }
  } else {
    lineColor = '#3bb2d0'
  }

  let energy = ''
  try {
    energy = a.end_speed
      ? kineticEnergy(
          a.end_speed,
          +vehicleTypes
            .find(item => item.vehicle === a.vehicle)
            .mass.split(' ')[0],
          a.units
        ).toFixedDown(2)
      : ''
  } catch (error) {
    console.log(
      `Could not calculate energy. Is the vehicle type valid?: ${error.stack}`
    )
  }

  return {
    ...a,
    line_color: lineColor,
    end_speed: a.end_speed ? a.end_speed.toFixedDown(2) : '',
    energy: energy,
    isEditMode: false,
  }
}

const tableStyle = {
  minWidth: 350,
}

export default function AttackTable() {
  //const classes = useStyles()
  const {
    attacks,
    selectedAttack,
    remoteStorage,
    selectedScenarioId,
    projects,
    selectedProjectId,
  } = useSelector(state => state)
  const [rows, setRows] = useState([])
  const dispatch = useDispatch()
  const [saveAttack] = useMutation(SAVE_ATTACK)
  const [saveAttackByGuid] = useMutation(SAVE_ATTACK_BY_GUID)
  const selectedRowRef = useRef(null)

  const units = projects.filter(p => p.id === selectedProjectId)[0].units

  useEffect(() => {
    if (attacks.length > 0) {
      const data = attacks
        .filter(attack => attack.scenario_id === selectedScenarioId)
        .map(a => createData(a))
      setRows(data)
    } else {
      setRows([])
    }
  }, [attacks, selectedScenarioId])

  useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [selectedAttack])

  const saveAttackToDatabase = async input => {
    console.log(input)
    await saveAttack({ variables: { input: input } })
  }

  const saveAttackColorByGuid = async input => {
    await saveAttackByGuid({ variables: { input } })
  }

  const onDone = id => {
    setRows(() => {
      return rows.map(row => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode }
        }
        return row
      })
    })
    let data = rows.filter(row => row.id === id)[0]
    dispatch(updateAttackData(id, data))
    if (remoteStorage.attacks) {
      const input = {
        id: id.toString(),
        name: data.name,
        start_speed: parseInt(data.speed),
        end_speed: parseInt(data.speed),
        path_length: parseInt(data.path_length),
        vehicle: data.vehicle,
        line_color: data.line_color,
        recalculated_id: data.recalculated_id,
        original_id: data.original_id,
      }
      saveAttackToDatabase(input)
    }
  }

  const onToggleEditMode = id => {
    setRows(() => {
      return rows.map(row => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode }
        }
        return row
      })
    })
  }

  const onChange = (e, row) => {
    const value = e.target.value
    const name = e.target.name
    const { id } = row
    updateRows(id, name, value)
  }

  const handleVehicleChange = (e, values, row) => {
    const { id } = row
    updateRows(id, 'vehicle', values.vehicle)
  }

  const handleActiveChange = (event, row) => {
    const { id } = row
    updateRows(id, 'active', event.target.checked)
  }

  const handleLineColorChange = async (event, row) => {
    const color = event.target.value
    updateRows(row.id, 'line_color', color)

    try {
      const parsedGeojson = JSON.parse(row.geojson || '{}')
      const updatedGeojson = {
        ...parsedGeojson,
        properties: {
          ...(parsedGeojson.properties || {}),
          line_color: color,
        },
      }
      const geojsonString = JSON.stringify(updatedGeojson)

      dispatch(
        updateAttackDataByGuid(row.geom_guid, {
          geojson: geojsonString,
          line_color: color,
        })
      )

      if (remoteStorage.attacks) {
        await saveAttackColorByGuid({
          geom_guid: row.geom_guid,
          geojson: geojsonString,
          line_color: color,
        })
      }
    } catch (e) {
      console.error('Failed to update attack line color', e)
    }
  }

  const getRowStyle = row => {
    let style = {}
    selectedAttack && row.geom_guid === selectedAttack.geom_guid
      ? (style['background'] = '#EEEEEE')
      : null
    return style
  }

  const updateRows = (rowId, field, value) => {
    const newRows = rows.map(row => {
      if (row.id === rowId) {
        return { ...row, [field]: value }
      }
      return row
    })
    setRows(newRows)
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={tableStyle} size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">Name</TableCell>
            <TableCell align="center">{`Start Speed (${units.speedUnits})`}</TableCell>
            <TableCell align="center">{`End Speed (${units.speedUnits})`}</TableCell>
            <TableCell align="center">{`Path Length (${units.distanceUnits})`}</TableCell>
            <TableCell align="center">Vehicle Type</TableCell>
            <TableCell align="center">Line Color</TableCell>
            <TableCell align="center">Energy (kJ)</TableCell>
            <TableCell align="center">Active</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.id}
              style={getRowStyle(row)}
              ref={
                selectedAttack?.geom_guid === row.geom_guid
                  ? selectedRowRef
                  : null
              }
            >
              <CustomTableCell {...{ row, name: 'name', onChange }} />
              <CustomTableCell {...{ row, name: 'start_speed', onChange }} />
              <CustomTableCell {...{ row, name: 'end_speed', onChange }} />
              <CustomTableCell {...{ row, name: 'path_length', onChange }} />
              <CustomTableCell
                {...{ row, name: 'vehicle', onChange: handleVehicleChange }}
              />
              <TableCell align="center">
                {row.original_id ? (
                  <input
                    type="color"
                    aria-label="Line color"
                    value={row.line_color || '#ff0000'}
                    onChange={e => handleLineColorChange(e, row)}
                    style={{
                      width: 30,
                      height: 24,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  />
                ) : null}
              </TableCell>
              <CustomTableCell {...{ row, name: 'energy', onChange }} />
              <CustomTableCell
                {...{ row, name: 'active', onChange: handleActiveChange }}
              />
              <TableCell>
                {row.isEditMode ? (
                  <>
                    <IconButton
                      size="small"
                      aria-label="done"
                      onClick={() => onDone(row.id)}
                    >
                      <DoneIcon />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    aria-label="edit"
                    onClick={() => onToggleEditMode(row.id)}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
